import { useQuery } from '@tanstack/react-query'
import { Barcode, PackageSearch, Search } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/app/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog'
import { GlobalPaginator } from '@/app/components/ui/global-paginator'
import { Input } from '@/app/components/ui/input'
import { promotionService, type PromotionListItem } from '@/app/services/promotion.service'
import { useDebounce } from '@/hooks/useDebounce'

const matchedLabels = {
  product: 'Producto específico',
  category: 'Categoría',
  department: 'Departamento',
}

interface Props {
  promotion: PromotionListItem | null
  onClose: () => void
}

export function PromotionProductsDialog({ promotion, onClose }: Props) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search, 300)
  const query = useQuery({
    queryKey: ['promotion-products', promotion?.id, page, debouncedSearch],
    queryFn: () => promotionService.applicableProducts(promotion!.id, {
      page,
      per_page: 20,
      search: debouncedSearch || undefined,
    }),
    enabled: promotion !== null,
    placeholderData: (previous) => previous,
  })

  const close = () => {
    setSearch('')
    setPage(1)
    onClose()
  }

  return (
    <Dialog open={promotion !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto bg-popover sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackageSearch className="h-5 w-5" />Productos aplicables: {promotion?.name}</DialogTitle>
          <DialogDescription>
            Coincidencias dinámicas por productos, categorías y departamentos. Una exclusión directa no aparece en esta lista.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <label className="relative block flex-1 text-sm">
            <span className="mb-1 block font-medium text-popover-foreground">Buscar en esta promoción</span>
            <Search className="absolute bottom-2.5 left-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1) }}
              placeholder="Nombre, SKU o código de barras..."
              className="pl-9"
            />
          </label>
          <p className="pb-2 text-sm text-muted-foreground">{query.data?.total ?? promotion?.applicable_products_count ?? 0} productos alcanzados</p>
        </div>

        <div className={`overflow-hidden rounded-lg border border-border transition-opacity ${query.isFetching ? 'opacity-60' : 'opacity-100'}`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Categoría</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Departamento</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Coincide por</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {query.isPending && <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">Consultando productos...</td></tr>}
                {query.isError && <tr><td colSpan={5} className="px-6 py-12 text-center text-destructive">No fue posible cargar los productos.</td></tr>}
                {query.data?.data.map((product) => (
                  <tr key={product.id} className="hover:bg-table-row-hover">
                    <td className="px-4 py-3">
                      <p className="font-medium text-table-foreground">{product.name}</p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Barcode className="h-3 w-3" />{product.barcode || product.sku || 'Sin código'}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-table-foreground">{product.category?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-table-foreground">{product.department?.name || '—'}</td>
                    <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{product.matched_by.map((match) => <Badge key={match} variant="secondary">{matchedLabels[match]}</Badge>)}</div></td>
                    <td className="px-4 py-3"><Badge className={product.is_active === false ? 'bg-[#FFE2E2] text-[#C10008]' : 'bg-[#DCFCE6] text-[#008438]'}>{product.is_active === false ? 'Inactivo' : 'Activo'}</Badge></td>
                  </tr>
                ))}
                {!query.isPending && !query.isError && query.data?.data.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">No hay productos coincidentes.</td></tr>}
              </tbody>
            </table>
          </div>
          {query.data && query.data.total > 0 && <GlobalPaginator totalItems={query.data.total} itemsPerPage={query.data.per_page} currentPage={page} from={query.data.from ?? 0} to={query.data.to ?? 0} onPageChange={setPage} />}
        </div>
      </DialogContent>
    </Dialog>
  )
}
